import loadable from '@loadable/component'
import { matchRoutes, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { useLayoutEffect, useMemo } from 'react'

import TripGuard from './components/TripGuard/TripGuard'
import RootWrapper from './pages/RootWrapper'
import Trip404Page from './pages/Trip404Page'
import type { TripTab } from './utils/tabs'
import TripTabRedirect from './pages/TripTabRedirect'

import AuthRouteElement from 'components/App/Routes/AuthRouteElement'
import Redirect from 'tripPlanner/components/Redirect'
import { CHAT_TRIP_PLANNER_PATH } from 'tripPlanner/config'
import WebViewRoutes from 'tripPlanner/pages/WebViews/WebViewRoutes'
import UrlLocation from 'src/lib/url/UrlLocation'
import { useAppDispatch } from 'hooks/reduxHooks'
import { registerPageView } from 'actions/AnalyticsActions'

const PublicTripParent = loadable(
  () =>
    import(
      'tripPlanner/pages/publicTrip/PublicTripParent'
    ),
)

const ChatTripPlannerPage = loadable(
  () =>
    import(
      'tripPlanner/pages/chat/ChatTripPlannerPage'
    ),
)

const TripParent = loadable(
  () =>
    import(
      'tripPlanner/pages/trip/TripParent'
    ),
)

const AcceptInvite = loadable(
  () =>
    import(
      './pages/trip/AcceptInvite'
    ),
)

const HiddenSettings = loadable(
  () =>
    import(
      './pages/hiddenSettings/HiddenSettings'
    ),
)

const tripTabs: Array<Lowercase<TripTab>> = ['summary', 'itinerary', 'explore', 'documents']
const publicTripTabs = ['summary', 'itinerary']

function isTripTab(tab: string | undefined): boolean {
  return !!tab && tripTabs.includes(tab.toLowerCase() as Lowercase<TripTab>)
}

function isPublicTripTab(tab: string | undefined): boolean {
  return !!tab && publicTripTabs.includes(tab.toLowerCase())
}

function TripRouteGuard() {
  const { tab } = useParams<'tab'>()

  if (!isTripTab(tab)) {
    return <Trip404Page />
  }

  return <TripParent />
}

function PublicTripRouteGuard() {
  const { tab } = useParams<'tab'>()

  if (!isPublicTripTab(tab)) {
    return <Trip404Page />
  }

  return <PublicTripParent />
}

const tripPlannerRoutes = [
  { path: '/:regionCode/trip-planner' },
  { path: '/:regionCode/trip-planner/generate-trip' },
  { path: '/:regionCode/trip-planner/chat-trip-planner/:chatId?' },
  { path: '/:regionCode/trip-planner/trip/:tripId/:tab' },
  { path: '/:regionCode/trip-planner/public-trip/:tripId/:tab' },
  { path: '/:regionCode/trip-planner/trip/:tripId/accept-invite/:inviteId' },
  { path: '/:regionCode/trip-planner/hidden-settings' },
  { path: '/:regionCode/trip-planner/webView/*' },
  { path: '/:regionCode/trip-planner/trip/:tripId' },
]

function TripPlannerRoutes() {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const localeStrippedLocation = useMemo(() => {
    return {
      ...location,
      pathname: new UrlLocation(location.pathname).withoutLocale().path,
    }
  }, [location])
  const matchedRoute = useMemo(() => {
    return matchRoutes(tripPlannerRoutes, localeStrippedLocation)?.at(-1)
  }, [localeStrippedLocation])

  useLayoutEffect(() => {
    if (!matchedRoute) return

    dispatch(registerPageView({
      template: matchedRoute.route.path,
      params: matchedRoute.params,
      path: matchedRoute.pathname,
      search: location.search,
    }))
  }, [dispatch, location.search, matchedRoute])

  return (
    <TripGuard fallback={<Trip404Page />}>
      <RootWrapper>
        <Routes location={localeStrippedLocation}>
          <Route path="about" element={<Redirect to={CHAT_TRIP_PLANNER_PATH} />} />
          <Route index element={<Redirect to={CHAT_TRIP_PLANNER_PATH} />} />
          <Route path="generate-trip" element={<ChatTripPlannerPage />} />
          <Route path="chat-trip-planner/:chatId?" element={<ChatTripPlannerPage />} />
          <Route
            path="trip/:tripId/:tab"
            element={(
              <AuthRouteElement showPopup path="/:regionCode/trip-planner/trip/:tripId/:tab">
                <TripRouteGuard />
              </AuthRouteElement>
            )}
          />
          <Route
            path="public-trip/:tripId/:tab"
            element={<PublicTripRouteGuard />}
          />
          <Route
            path="trip/:tripId/accept-invite/:inviteId"
            element={(
              <AuthRouteElement showPopup path="/:regionCode/trip-planner/trip/:tripId/accept-invite/:inviteId">
                <AcceptInvite />
              </AuthRouteElement>
            )}
          />
          <Route
            path="hidden-settings"
            element={(
              <AuthRouteElement showPopup path="/:regionCode/trip-planner/hidden-settings">
                <HiddenSettings />
              </AuthRouteElement>
            )}
          />
          <Route path="webView/*" element={<WebViewRoutes />} />
          <Route
            path="trip/:tripId"
            element={<TripTabRedirect />}
          />
          <Route path="*" element={<Trip404Page />} />
        </Routes>
      </RootWrapper>
    </TripGuard>
  )
}

export default TripPlannerRoutes
